export interface IJitsiMeetOptions {
  roomName: string;
  width?: number | string;
  height?: number | string;
  parentNode?: Element;
  jwt?: string;
  lang?: string;
  configOverwrite?: IJitsiConfigOverride;
}

export interface IJitsiConfigOverride {
  prejoinPageEnabled?: boolean;
  hideDisplayName?: boolean;
  participantsPane?: {
    hideModeratorSettingsTab?: boolean;
    hideMoreActionsButton?: boolean;
    hideMuteAllButton?: boolean;
  };
  p2p?: {
    enabled?: boolean;
  };
  hideConferenceSubject?: boolean;
  enableClosePage?: boolean;
  enableWelcomePage?: boolean;
  useHostPageLocalStorage?: boolean;
  disableProfile?: boolean;
  startWithAudioMuted?: boolean;
  startAudioOnly?: boolean;
  apiLogLevels?: string[];
  toolbarButtons?: string[];
  defaultLocalDisplayName?: string;
  defaultRemoteDisplayName?: string;
  prejoinConfig?: {
    enabled?: boolean;
    hideDisplayName?: boolean;
    hideExtraJoinButtons?: string[];
  };
  sandbox?: string;
}

export default class {
  parseMeetUrl(
    link: string,
    externalConfig?: IJitsiConfigOverride
  ): { options: IJitsiMeetOptions; domain: string } | null {
    try {
      const urlHash = link.split("#");
      urlHash.shift();
      const urlHashString = urlHash.join("#");
      const hashArray = urlHashString.split("&");
      const systemParamNames = [
        "jitsi_meet_external_api_id",
        "appData.localStorageContent"
      ];
      const configOverwrite: IJitsiConfigOverride = {
        apiLogLevels: ["debug", "error", "info", "warn"],
        enableClosePage: false,
        enableWelcomePage: false,
        toolbarButtons: [
          "camera",
          "chat",
          "closedcaptions",
          "desktop",
          "download",
          "embedmeeting",
          "etherpad",
          "feedback",
          "filmstrip",
          "fullscreen",
          "hangup",
          "help",
          "invite",
          "highlight",
          "linktosalesforce",
          "livestreaming",
          "microphone",
          "noisesuppression",
          "participants-pane",
          "profile",
          "raisehand",
          "recording",
          "security",
          "select-background",
          "settings",
          "shareaudio",
          "sharedvideo",
          "shortcuts",
          "stats",
          "tileview",
          "toggle-camera",
          "videoquality",
          "whiteboard"
        ]
      };
      const url = new URL(link);
      const options: IJitsiMeetOptions = {
        jwt: url.searchParams.get("jwt") || "",
        roomName: url.pathname.replace(/^\//, "")
      };
      const domain = url.host;
      hashArray.forEach(hashParam => {
        const paramArray = hashParam.split("=");
        if (
          paramArray[0] &&
          paramArray[0].length &&
          paramArray[1] &&
          paramArray[1].length
        ) {
          const name = paramArray[0];
          if (!systemParamNames.includes(name)) {
            paramArray.shift();
            const value = paramArray.join("=");
            if (name.startsWith("config.")) {
              configOverwrite[name.replace("config.", "")] = JSON.parse(value);
            } else {
              options[name] = JSON.parse(value);
            }
          }
        }
      });
      return {
        options: {
          ...options,
          configOverwrite: {
            ...configOverwrite,
            ...externalConfig
          }
        },
        domain
      };
    } catch (_) {
      return null;
    }
  }

  randomIntegerFromRange(min: number, max: number): number {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  generateRoomId(): string {
    let result = "";
    const characters = [
      "a",
      "b",
      "c",
      "d",
      "e",
      "f",
      "g",
      "h",
      "i",
      "j",
      "k",
      "l",
      "m",
      "n",
      "o",
      "p",
      "q",
      "r",
      "s",
      "t",
      "u",
      "v",
      "w",
      "x",
      "y",
      "z"
    ];

    while (result.length !== 12) {
      if ([3, 8].includes(result.length)) {
        result += "-";
      }
      result +=
        characters[this.randomIntegerFromRange(0, characters.length - 1)];
    }

    return result;
  }

  pause(length: number): Promise<void> {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve();
      }, length);
    });
  }

  validateRoomId(roomId: string): boolean {
    return (
      /^[a-z0-9][a-z0-9\-_.]{1,48}[a-z0-9]$/.test(roomId) &&
      !/[-_.]{2,}/.test(roomId)
    );
  }
}
